import type {
  ProfileUserResponse,
  UserState,
  RegisterFormData,
  B2bUserData,
  CuisineTypesResponse,
  BusinessActivityResponse,
  CityResponse,
  RequestCityPayload,
  UserAuthResponse,
  CompanyDocumentsResponse,
  CompanyDocumentsPayload,
} from "~/types/Data/UserManagement/b2bUser";
import type {
  OtpChallengeResponse,
  SmsVerifyResponse,
  EmailVerifyResponse,
  ProfileVerifyResponse,
} from "~/types/Data/UserManagement/otpAuth";
import type { MobileFieldOutput } from "~/types/schemas/User/Atom/mobileField";
import paths from "~/constants/paths";
import { objToFormData } from "~/utils/common/objects";
import useCart from "~/composables/Checkout/useCart";
import { extractErrorMessage } from "~/composables/Common/useApi";

export default function useB2bUser() {
  const user = useState<UserState | null>("b2b-user", () => null);
  const loading = ref(false);
  const error = ref("");

  const router = useRouter();
  const userToken = useCookie("hs-token");
  const registrationId = useSessionStorage<string>("registration-id", "");
  const verifiedEmail = useSessionStorage<string>("verified-email", "");
  const verifiedMobile = useSessionStorage<Partial<MobileFieldOutput>>(
    "verified-mobile",
    {},
  );

  const { addToast } = useToast();
  const { cartCookie, resetCart, claimCart } = useCart();

  const setupUser = (b2bUser: B2bUserData, accessToken: string) => {
    userToken.value = accessToken;
    user.value = {
      ...b2bUser,
      token: accessToken,
    };

    verifiedEmail.value = user.value?.email;
    verifiedMobile.value = {
      country_code: user.value?.country_code,
      number: user.value?.mobile_number,
    };
  };

  const initUser = async (baseUrl: string) => {
    if (userToken.value) {
      loading.value = true;
      error.value = "";
      try {
        const { res: profileRes } = await useApi<ProfileUserResponse>(
          "/api/v1/b2b-user/profile",
          {
            authToken: userToken.value,
          },
          {
            baseUrl,
          },
        );

        if (!profileRes?.data) {
          throw new Error("User profile data is missing");
        }

        setupUser(profileRes.data, userToken.value);

        if (profileRes.data.user_type === "Company Admin") {
          await fetchCompanyDocs(baseUrl);
        }
      } catch (err) {
        logout();
        router.push(paths.login);
      } finally {
        loading.value = false;
      }
    }
  };

  const createSmsChallenge = async (
    mobile_number: string,
    country_code: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<OtpChallengeResponse>(
      "/api/v1/otp/sms/challenges",
      {
        method: "POST",
        body: {
          mobile_number,
          country_code,
        },
      },
      { catchError: true },
    );

    loading.value = false;

    return {
      challenge: res?.data,
      error: extractErrorMessage(err?.meta?.errors),
    };
  };

  const verifySmsChallenge = async (
    challengeId: string,
    code: string,
    mobile_number: string,
    country_code: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<SmsVerifyResponse>(
      `/api/v1/otp/sms/challenges/${challengeId}/verify`,
      {
        method: "POST",
        body: { code },
      },
      { catchError: true },
    );

    if (res?.data && res.data.registration_id === null) {
      setupUser(res.data.b2b_user, res.data.access_token);

      if (res.data.b2b_user.delete_request.is_deletion_pending) {
        router.push(paths.accountDeletionPending);
      } else {
        if (res.data.b2b_user.user_type === "Company Admin") {
          await fetchCompanyDocs();
        }
        router.push(paths.home);
      }
    } else if (res?.data) {
      registrationId.value = res.data.registration_id;
      verifiedMobile.value = { country_code, number: mobile_number };
    }

    loading.value = false;

    return { data: res?.data, error: extractErrorMessage(err?.meta?.errors) };
  };

  const createEmailChallenge = async (
    registration_id: string,
    email: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<OtpChallengeResponse>(
      "/api/v1/otp/email/challenges",
      {
        method: "POST",
        body: { registration_id, email },
      },
      { catchError: true },
    );

    loading.value = false;

    return {
      challenge: res?.data,
      error: extractErrorMessage(err?.meta?.errors),
    };
  };

  const verifyEmailChallenge = async (
    registration_id: string,
    challengeId: string,
    code: string,
    email: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<EmailVerifyResponse>(
      `/api/v1/otp/email/challenges/${challengeId}/verify`,
      {
        method: "POST",
        body: { registration_id, code },
      },
      { catchError: true },
    );

    if (res?.data?.email_verified) {
      verifiedEmail.value = email;
    }

    loading.value = false;

    return { data: res?.data, error: extractErrorMessage(err?.meta?.errors) };
  };

  const register = async (userData: RegisterFormData) => {
    const formData = objToFormData({
      ...userData,
      registration_id: registrationId.value,
      mobile_number: verifiedMobile.value?.number,
      country_code: verifiedMobile.value?.country_code,
      email: verifiedEmail.value,
    });
    loading.value = true;

    const { res, err } = await useApi<UserAuthResponse>(
      "/api/v1/otp/register",
      {
        method: "POST",
        body: formData,
      },
      {
        catchError: true,
      },
    );

    if (err) {
      error.value =
        extractErrorMessage((err as any)?.meta?.errors) ??
        "Registration failed";
    } else if (res?.data?.b2b_user && res.data.access_token) {
      setupUser(res.data.b2b_user, res.data.access_token);
      if (cartCookie.value) claimCart(cartCookie.value, res.data.access_token);
      registrationId.value = "";
    } else {
      error.value = "Registration response is missing user data";
    }

    loading.value = false;

    return err || !res?.data?.b2b_user ? undefined : res.data;
  };

  const logout = () => {
    user.value = null;
    userToken.value = null;
    resetCart();
    verifiedMobile.value = null;
    verifiedEmail.value = null;
    registrationId.value = "";
  };

  const updateProfile = async (
    userData: Partial<B2bUserData>,
    companyDocs?: CompanyDocumentsPayload,
    options?: { disableToast?: boolean },
  ) => {
    if (!userToken.value) return;

    loading.value = true;

    try {
      const { res } = await useApi<ProfileUserResponse>(
        "/api/v1/b2b-user/update",
        {
          method: "PUT",
          authToken: userToken.value,
          body: { ...userData, city_id: userData.city },
        },
      );

      if (res?.data && user.value) {
        user.value = {
          ...user.value,
          ...res.data,
        };

        verifiedEmail.value = user.value.email;
        verifiedMobile.value = {
          country_code: user.value.country_code,
          number: user.value.mobile_number,
        };

        if (companyDocs && user.value.user_type === "Company Admin") {
          if (user.value.has_documents) await updateCompanyDocs(companyDocs);
          else await verifyCompanyDocs(companyDocs);
        }
      }

      if (!options?.disableToast)
        addToast("Profile updated successfully.", "success");
    } catch (error: any) {
      const data = error.response?._data;
      const message =
        extractErrorMessage(data?.meta?.errors) ?? "Failed to update profile.";

      if (!options?.disableToast) addToast(message, "error");
    } finally {
      loading.value = false;
    }
  };

  const createProfileEmailChallenge = async (email: string) => {
    loading.value = true;

    const { res, err } = await useApi<OtpChallengeResponse>(
      "/api/v1/b2b-user/profile/otp/email/challenges",
      {
        method: "POST",
        authToken: userToken.value,
        body: { email },
      },
      { catchError: true },
    );

    loading.value = false;

    return {
      challenge: res?.data,
      error: extractErrorMessage(err?.meta?.errors),
    };
  };

  const verifyProfileEmailChallenge = async (
    challengeId: string,
    code: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<ProfileVerifyResponse>(
      `/api/v1/b2b-user/profile/otp/email/challenges/${challengeId}/verify`,
      {
        method: "POST",
        authToken: userToken.value,
        body: { code },
      },
      { catchError: true },
    );

    loading.value = false;

    return { data: res?.data, error: extractErrorMessage(err?.meta?.errors) };
  };

  const createProfileSmsChallenge = async (
    country_code: string,
    mobile_number: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<OtpChallengeResponse>(
      "/api/v1/b2b-user/profile/otp/sms/challenges",
      {
        method: "POST",
        authToken: userToken.value,
        body: { country_code, mobile_number },
      },
      { catchError: true },
    );

    loading.value = false;

    return {
      challenge: res?.data,
      error: extractErrorMessage(err?.meta?.errors),
    };
  };

  const verifyProfileSmsChallenge = async (
    challengeId: string,
    code: string,
  ) => {
    loading.value = true;

    const { res, err } = await useApi<ProfileVerifyResponse>(
      `/api/v1/b2b-user/profile/otp/sms/challenges/${challengeId}/verify`,
      {
        method: "POST",
        authToken: userToken.value,
        body: { code },
      },
      { catchError: true },
    );

    loading.value = false;

    return { data: res?.data, error: extractErrorMessage(err?.meta?.errors) };
  };

  const fetchCompanyDocs = async (baseUrl?: string) => {
    try {
      const { res: documentsRes } = await useApi<CompanyDocumentsResponse>(
        "/api/v1/b2b-user/company/documents",
        {
          authToken: userToken.value,
        },
        {
          baseUrl,
        },
      );

      if (!documentsRes?.data) {
        return null;
      }

      if (documentsRes?.data && user.value) {
        user.value = {
          ...user.value,
          ...documentsRes.data,
        };
      }
      return documentsRes.data;
    } catch (err) {
      console.error("Failed to fetch company documents:", err);
      return null;
    }
  };

  const updateCompanyDocs = async (userData: CompanyDocumentsPayload) => {
    const body = objToFormData(userData);

    const { res } = await useApi<CompanyDocumentsResponse>(
      "/api/v1/b2b-user/company/update-documents",
      {
        method: "POST",
        authToken: userToken.value,
        body,
      },
    );

    if (res?.data && user.value) {
      user.value = {
        ...user.value,
        ...res.data,
      };
    }

    return res;
  };

  const verifyCompanyDocs = async (payload: CompanyDocumentsPayload) => {
    const body = objToFormData(payload);

    const { res } = await useApi<CompanyDocumentsResponse>(
      "/api/v1/b2b-user/company/verify",
      {
        method: "POST",
        authToken: userToken.value,
        body,
      },
    );
    if (res?.data && user.value) {
      user.value = {
        ...user.value,
        ...res.data,
      };
    }

    return res?.data;
  };

  const getCuisineTypes = async () => {
    const { res } = await useApi<CuisineTypesResponse>(
      "/api/v1/b2b-user/cuisine-types",
    );

    return res?.data;
  };

  const getBusinessActivities = async () => {
    const { res, err } = await useApi<BusinessActivityResponse>(
      "/api/v1/b2b-user/business-activities",
      {},
      { catchError: true },
    );

    return {
      data: res?.data ?? [],
      error: extractErrorMessage(err?.meta?.errors),
    };
  };

  const fetchCities = async () => {
    const { res } = await useApi<CityResponse>("/api/v1/city");
    return res?.data ?? [];
  };

  const requestNewCity = async (
    userData: Omit<RequestCityPayload, "mobile_number" | "country_code">,
  ) => {
    const formData = objToFormData({
      ...userData,
      mobile_number: verifiedMobile.value?.number,
      country_code: verifiedMobile.value?.country_code,
      email: verifiedEmail.value,
    });

    const { err } = await useApi(
      "/api/v1/request-new-city",
      {
        method: "POST",
        body: formData,
      },
      { catchError: true },
    );

    return { error: extractErrorMessage(err?.meta?.errors) };
  };

  const validateMobile = async (country_code: string, mobile: string) => {
    await useApi(`/api/v1/validate`, {
      method: "POST",
      body: {
        country_code,
        mobile,
      },
    });
  };

  const deleteAccount = async (message: string) => {
    await useApi(`/api/v1/delete-user`, {
      method: "POST",
      authToken: userToken.value,
      body: {
        feedback: message,
      },
    });
  };

  const cancelDeleteRequest = async (deleteRequestId: string) => {
    if (!userToken.value) return;

    try {
      await useApi(`/api/v1/delete-requests/${deleteRequestId}`, {
        method: "DELETE",
        authToken: userToken.value,
      });

      router.replace(paths.home);

      addToast("Account deletion request cancelled successfully.", "success");
    } catch (err: any) {
      const message =
        err.response?._data?.meta?.errors?.[0]?.message ||
        "Failed to cancel deletion request";
      addToast(message, "error");
      throw err;
    }
  };

  return {
    user,
    loading,
    error,
    registrationId,
    initUser,
    createSmsChallenge,
    verifySmsChallenge,
    createEmailChallenge,
    verifyEmailChallenge,
    register,
    logout,
    updateProfile,
    fetchCompanyDocs,
    verifyCompanyDocs,
    updateCompanyDocs,
    createProfileEmailChallenge,
    verifyProfileEmailChallenge,
    createProfileSmsChallenge,
    verifyProfileSmsChallenge,
    getCuisineTypes,
    getBusinessActivities,
    fetchCities,
    requestNewCity,
    validateMobile,
    deleteAccount,
    cancelDeleteRequest,
  };
}
